import { useEffect, useState } from 'react';
import { prefersReducedMotion } from '@/lib/dom';
import { reboot } from '@/shell/Boot/reboot';
import { installCrashRoulette } from './roulette';
import type { Bugcheck } from './bugcheck';
import { useCrashStore } from './store';
import css from './Bsod.module.css';

/** How long the memory dump takes to reach 100. */
const DUMP_MS = 4_000;

/** And how long the finished screen stands before XP restarts itself. */
const LINGER_MS = 7_000;

/**
 * Nothing may dismiss the screen for this long. The pointerdown that brought
 * the machine down is over by the time this mounts, but a visitor mid-click on
 * something else should still get to read what happened.
 */
const ARM_MS = 1_200;

/**
 * The stop screen.
 *
 * Silent, which is the period-correct part people find hardest to believe: a
 * bugcheck happens below the level that knows what a sound scheme is, so an XP
 * machine went blue without a peep. There is no `play()` call here on purpose.
 *
 * "Automatically restart" was ticked by default in XP, so it does — but slowly
 * enough to read, and a click or a key restarts it at once.
 */
export function Bsod() {
  const bugcheck = useCrashStore((s) => s.bugcheck);

  useEffect(() => installCrashRoulette(), []);

  if (!bugcheck) return null;
  return <StopScreen key={bugcheck.code} bugcheck={bugcheck} />;
}

/**
 * Split out so the dump timers mount with the screen rather than with the
 * desktop — this way they simply do not exist until something has crashed.
 */
function StopScreen({ bugcheck }: { bugcheck: Bugcheck }) {
  const reduced = prefersReducedMotion();
  const [percent, setPercent] = useState(reduced ? 100 : 0);

  /* The dump counter. XP wrote it in place, one line, counting to 100. */
  useEffect(() => {
    if (reduced) return;
    const started = performance.now();
    const frame = window.setInterval(() => {
      const done = Math.min(1, (performance.now() - started) / DUMP_MS);
      setPercent(Math.floor(done * 100));
      if (done >= 1) window.clearInterval(frame);
    }, 80);
    return () => window.clearInterval(frame);
  }, [reduced]);

  /* Restart: automatically once the dump is written, or on any input. */
  useEffect(() => {
    const timer = window.setTimeout(reboot, DUMP_MS + LINGER_MS);

    let armed = false;
    const arm = window.setTimeout(() => {
      armed = true;
    }, ARM_MS);

    const bail = () => {
      if (armed) reboot();
    };
    window.addEventListener('pointerdown', bail, { capture: true });
    window.addEventListener('keydown', bail, { capture: true });

    return () => {
      window.clearTimeout(timer);
      window.clearTimeout(arm);
      window.removeEventListener('pointerdown', bail, { capture: true });
      window.removeEventListener('keydown', bail, { capture: true });
    };
  }, []);

  const { driver } = bugcheck;

  return (
    <div className={css.screen} role="alert" aria-label="Windows has encountered a fatal error">
      <div className={css.text}>
        <p>
          A problem has been detected and Windows has been shut down to prevent damage to your
          computer.
        </p>

        <p>{bugcheck.name}</p>

        <p>
          If this is the first time you&apos;ve seen this Stop error screen, restart your computer.
          If this screen appears again, follow these steps:
        </p>

        <p>
          Check to make sure any new hardware or software is properly installed. If this is a new
          installation, ask your hardware or software manufacturer for any Windows updates you
          might need.
        </p>

        <p>
          If problems continue, disable or remove any newly installed hardware or software. Disable
          BIOS memory options such as caching or shadowing. If you need to use Safe Mode to remove
          or disable components, restart your computer, press F8 to select Advanced Startup
          Options, and then select Safe Mode.
        </p>

        <p>Technical information:</p>

        <p>
          {`*** STOP: ${bugcheck.code} (0x${bugcheck.args.join(',0x')})`}
          {driver && (
            <>
              {'\n\n'}
              {`***  ${driver.name} - Address ${driver.address} base at ${driver.base}, DateStamp ${driver.dateStamp}`}
            </>
          )}
        </p>

        <p>
          {'Beginning dump of physical memory\n'}
          {percent < 100
            ? `Dumping physical memory to disk: ${percent}`
            : 'Physical memory dump complete.'}
        </p>

        <p>
          Contact your system administrator or technical support group for further assistance.
        </p>
      </div>
    </div>
  );
}
